import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import OrderManager from '../services/dashboard/OrderManager';

const initialState = {
  userOrders: [],
  adminOrders: [],
  pendingOrders: [],
  returnOrders: [],
  orderDetails: null,
  status: 'idle',
  error: null,
};

// Utility function
const ensureArray = (data) => Array.isArray(data) ? data : (data ? [data] : []);

export const fetchUserOrders = createAsyncThunk('orders/fetchUserOrders', async (userId) => {
  return await OrderManager.getOrdersByUser(userId);
});

export const fetchAdminOrders = createAsyncThunk('orders/fetchAdminOrders', async (pageable) => {
  return await OrderManager.getAllOrders(pageable);
});

export const fetchPendingOrders = createAsyncThunk('orders/fetchPendingOrders', async () => {
  return await OrderManager.getPendingOrders();
});

export const fetchReturnOrders = createAsyncThunk('orders/fetchReturnOrders', async () => {
  return await OrderManager.getReturnOrders();
});

export const createNewOrder = createAsyncThunk('orders/createNewOrder', async ({ userId, bookId }) => {
  return await OrderManager.createOrder(userId, bookId);
});

export const createOrdersFromCart = createAsyncThunk(
  'orders/createOrdersFromCart',
  async ({ userId, bookIds }) => {
    return await OrderManager.createOrdersFromCart(userId, bookIds);
  }
);

export const cancelUserOrder = createAsyncThunk(
  'orders/cancelUserOrder',
  async ({ userId, orderId }) => {
    return await OrderManager.cancelUserOrder(userId, orderId);
  }
);

export const cancelUserReturnRequest = createAsyncThunk(
  'orders/cancelUserReturnRequest',
  async ({ userId, orderId }) => {
    return await OrderManager.cancelUserReturnRequest(userId, orderId);
  }
);


export const updateOrderStatus = createAsyncThunk('orders/updateOrderStatus', async ({ orderId, status }) => {
  return await OrderManager.updateOrderStatus(orderId, status);
});

export const updateDeliveryStatus = createAsyncThunk('orders/updateDeliveryStatus', async ({ orderId, deliveryStatus }) => {
  return await OrderManager.updateDeliveryStatus(orderId, deliveryStatus);
});

export const cancelOrder = createAsyncThunk('orders/cancelOrder', async (orderId) => {
  return await OrderManager.cancelOrder(orderId);
});

export const returnBook = createAsyncThunk('orders/returnBook', async ({ orderId, collectedBy }) => {
  return await OrderManager.returnBook(orderId, collectedBy);
});

export const adminSchedulePickup = createAsyncThunk('orders/adminSchedulePickup', async (orderId) => {
  return await OrderManager.adminSchedulePickup(orderId);
});

export const adminMarkReturnInTransit = createAsyncThunk('orders/adminMarkReturnInTransit', async (orderId) => {
  return await OrderManager.adminMarkReturnInTransit(orderId);
});

export const adminMarkBookReturned = createAsyncThunk('orders/adminMarkBookReturned', async ({ orderId, collectedBy }) => {
  return await OrderManager.adminMarkBookReturned(orderId, collectedBy);
});

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    resetOrders: (state) => {
      state.userOrders = [];
      state.adminOrders = [];
      state.pendingOrders = [];
      state.returnOrders = [];
      state.orderDetails = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserOrders.fulfilled, (state, { payload }) => {
        state.userOrders = ensureArray(payload);
        state.status = 'succeeded';
      })
      .addCase(fetchAdminOrders.fulfilled, (state, { payload }) => {
        state.adminOrders = ensureArray(payload);
        state.status = 'succeeded';
      })
      .addCase(fetchPendingOrders.fulfilled, (state, { payload }) => {
        state.pendingOrders = ensureArray(payload);
        state.status = 'succeeded';
      })
      .addCase(fetchReturnOrders.fulfilled, (state, { payload }) => {
        state.returnOrders = ensureArray(payload);
        state.status = 'succeeded';
      })
      .addCase(createNewOrder.fulfilled, (state, { payload }) => {
        state.userOrders.push(payload);
        state.status = 'succeeded';
      })
      .addCase(createOrdersFromCart.fulfilled, (state, { payload }) => {
        state.userOrders = [...state.userOrders, ...ensureArray(payload)];
        state.status = 'succeeded';
      })
      .addCase(updateOrderStatus.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(updateDeliveryStatus.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(cancelOrder.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(returnBook.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(adminSchedulePickup.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(adminMarkReturnInTransit.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(adminMarkBookReturned.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(cancelUserOrder.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addCase(cancelUserReturnRequest.fulfilled, (state, { payload }) => {
        state.orderDetails = payload;
        state.status = 'succeeded';
      })
      .addMatcher(
        (action) => action.type.startsWith('orders/') && action.type.endsWith('/pending'),
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('orders/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.status = 'failed';
          state.error = action.error.message;
        }
      );
  },
});

export const { resetOrders } = orderSlice.actions;
export default orderSlice.reducer;
